import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useHOA } from '../../contexts/HOAContext';
import { supabase } from '../../lib/supabase';
import { logEvent } from '../../lib/activityLogger';
import { withSupabaseQueryTimeout } from '../../lib/supabaseTimeout';
import { saveBoardMeeting } from '../../services/eventService';
import { notificationService } from '../../services/notificationService';
import { ConfirmDialog } from '../ui';
import { useModalDismiss } from '../ui/useModalDismiss';
import AudienceCheckboxGroup, { AUDIENCE_ROLES } from '../common/AudienceCheckboxGroup';

const DEFAULT_AUDIENCE = ['board', 'management', 'homeowner'];
const EVENT_TYPES = [
  { value: 'regular', label: 'Regular Meeting' },
  { value: 'special', label: 'Special Meeting' },
  { value: 'annual', label: 'Annual Meeting' },
  { value: 'executive', label: 'Executive Session' },
];

const emptyForm = {
  title: '',
  meeting_type: 'regular',
  date: '',
  time: '',
  location: '',
  meeting_url: '',
  visible_to_roles: DEFAULT_AUDIENCE,
};

const roleCanManage = (role) => ['admin', 'board', 'management'].includes(role);
const audienceForRole = (role) => (role === 'member' ? 'homeowner' : role);
const formatMeetingType = (value) => EVENT_TYPES.find((type) => type.value === value)?.label || value || 'Regular Meeting';

const formatInputDate = (isoValue) => {
  if (!isoValue) return '';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const formatInputTime = (isoValue) => {
  if (!isoValue) return '';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

const formatEventDate = (isoValue) => {
  if (!isoValue) return 'Date not scheduled';
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return 'Date not scheduled';
  return date.toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/Denver',
  });
};

const formatSupabaseError = (err) => (
  [err?.message, err?.details, err?.hint, err?.code].filter(Boolean).join(' ') || 'unknown error'
);

const sortUpcoming = (a, b) => {
  if (!a.scheduled_at && !b.scheduled_at) return String(a.title).localeCompare(String(b.title));
  if (!a.scheduled_at) return -1;
  if (!b.scheduled_at) return 1;
  return new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime();
};

const sortPast = (a, b) => new Date(b.scheduled_at).getTime() - new Date(a.scheduled_at).getTime();

const EventAudienceBadges = ({ roles = [] }) => {
  const visibleRoles = AUDIENCE_ROLES.filter((role) => roles.includes(role.value));
  if (visibleRoles.length === 0) return null;
  const title = `Visible to: ${visibleRoles.map((role) => role.label).join(', ')}`;

  return (
    <div className="flex flex-wrap items-center gap-2" title={title}>
      {visibleRoles.map((role) => (
        <span key={role.value} className="inline-flex items-center gap-1 text-amber-700 text-sm">
          <span className="material-symbols-outlined text-base">{role.icon}</span>
          <span className="text-[11px] font-bold uppercase tracking-widest">{role.label}</span>
        </span>
      ))}
    </div>
  );
};

const EventEmptyState = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30">
    <div className="w-16 h-16 bg-section-bg rounded-2xl flex items-center justify-center mb-6 warm-tactile-shadow">
      <span className="material-symbols-outlined text-3xl text-secondary">calendar_today</span>
    </div>
    <h3 className="text-xl font-bold text-primary-dark mb-2">No upcoming events scheduled</h3>
    <p className="text-sm font-medium text-slate-500 max-w-sm leading-relaxed">
      Board meetings and community events will appear here once scheduled.
    </p>
  </div>
);

const EventRow = ({ event, canManage, isPast, onEdit, onDelete }) => (
  <div className={`bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 p-5 ${isPast ? 'opacity-80' : ''}`}>
    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
      <div className="min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <h4 className={`text-lg font-bold ${isPast ? 'text-slate-500' : 'text-primary-dark'}`}>
            {event.title}
          </h4>
          <span className="px-3 py-1 text-[10px] uppercase tracking-widest font-bold bg-secondary/10 text-secondary rounded-full border border-secondary/20">
            {formatMeetingType(event.meeting_type)}
          </span>
        </div>
        {canManage && <EventAudienceBadges roles={event.visible_to_roles || []} />}
        <div className={`text-sm font-medium ${isPast ? 'text-slate-500' : 'text-slate-600'}`}>
          {formatEventDate(event.scheduled_at)}
          {event.location ? ` · ${event.location}` : ''}
        </div>
        {event.meeting_url && (
          <a
            href={event.meeting_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm font-bold text-secondary hover:brightness-110"
          >
            <span className="material-symbols-outlined text-base">link</span>
            Meeting link
          </a>
        )}
      </div>

      {canManage && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onEdit(event)}
            className="w-10 h-10 rounded-full border border-amber-100/40 bg-white hover:bg-section-bg text-primary-dark flex items-center justify-center transition-all"
            aria-label={`Edit ${event.title}`}
          >
            <span className="material-symbols-outlined text-xl">edit</span>
          </button>
          <button
            type="button"
            onClick={() => onDelete(event)}
            className="w-10 h-10 rounded-full border border-red-100 bg-white hover:bg-red-50 text-red-600 flex items-center justify-center transition-all"
            aria-label={`Delete ${event.title}`}
          >
            <span className="material-symbols-outlined text-xl">delete</span>
          </button>
        </div>
      )}
    </div>
  </div>
);

const EventManagement = () => {
  const { hoa, isLoading: hoaLoading } = useHOA();
  const { user, effectiveRole, impersonating, isLoading: authLoading } = useAuth();
  const hoaId = hoa?.id || user?.hoa_id;
  const displayRole = effectiveRole || user?.portalRole || 'member';
  const canManage = roleCanManage(displayRole);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pastOpen, setPastOpen] = useState(true);

  const loadEvents = useCallback(async () => {
    if (authLoading || hoaLoading) {
      setLoading(true);
      return;
    }

    if (!hoaId) {
      setLoading(false);
      setError('Community context is not ready. Please refresh or sign in again.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const { data, error: eventsError } = await withSupabaseQueryTimeout(
        () => supabase
          .from('board_meetings')
          .select('id, hoa_id, title, meeting_type, scheduled_at, location, meeting_url, status, visible_to_roles, created_by, created_at, updated_at')
          .eq('hoa_id', hoaId)
          .order('scheduled_at', { ascending: false })
          .range(0, 99),
        'Loading events',
        30000
      );

      if (eventsError) throw eventsError;
      setEvents(data || []);
    } catch (err) {
      setError(`Failed to load events: ${err.message || 'unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [authLoading, hoaLoading, hoaId]);
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    const reloadOnWake = () => {
      if (document.visibilityState === 'visible') loadEvents();
    };
    const reloadOnFocus = () => loadEvents();

    document.addEventListener('visibilitychange', reloadOnWake);
    window.addEventListener('focus', reloadOnFocus);
    window.addEventListener('online', reloadOnFocus);

    return () => {
      document.removeEventListener('visibilitychange', reloadOnWake);
      window.removeEventListener('focus', reloadOnFocus);
      window.removeEventListener('online', reloadOnFocus);
    };
  }, [loadEvents]);

  const visibleEvents = useMemo(() => {
    if (!impersonating && displayRole === 'admin') return events;
    const audienceRole = audienceForRole(displayRole);
    return events.filter((event) => (event.visible_to_roles || []).includes(audienceRole));
  }, [displayRole, events, impersonating]);

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const upcomingEvents = [];
    const pastEvents = [];
    visibleEvents.forEach((event) => {
      if (event.scheduled_at && new Date(event.scheduled_at) < now) {
        pastEvents.push(event);
      } else {
        upcomingEvents.push(event);
      }
    });
    return {
      upcoming: upcomingEvents.sort(sortUpcoming),
      past: pastEvents.sort(sortPast),
    };
  }, [visibleEvents]);

  const openCreate = () => {
    setEditingEvent(null);
    setForm(emptyForm);
    setIsFormOpen(true);
  };

  const openEdit = (event) => {
    setEditingEvent(event);
    setForm({
      title: event.title || '',
      meeting_type: event.meeting_type || 'regular',
      date: formatInputDate(event.scheduled_at),
      time: formatInputTime(event.scheduled_at),
      location: event.location || '',
      meeting_url: event.meeting_url || '',
      visible_to_roles: event.visible_to_roles?.length ? event.visible_to_roles : DEFAULT_AUDIENCE,
    });
    setIsFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setIsFormOpen(false);
    setEditingEvent(null);
    setForm(emptyForm);
  };

  useModalDismiss({ isOpen: isFormOpen, onClose: closeForm });

  const saveEvent = async (event) => {
    event.preventDefault();
    if (!hoaId || form.visible_to_roles.length === 0) return;
    setSaving(true);
    setError('');

    try {
      const { data: savedEvent, error: saveError, payload } = await saveBoardMeeting({
        hoaId,
        userId: user?.id,
        form,
        editingEventId: editingEvent?.id,
      });

      if (saveError) throw saveError;

      if (editingEvent) {
        logEvent('event_edited', { event_id: editingEvent.id, event_title: payload.title });
      } else {
        logEvent('event_created', {
          event_title: payload.title,
          event_date: payload.scheduled_at || '',
        });

        if (savedEvent) {
          try {
            await notificationService.notifyEventCreated({ event: savedEvent, actorUserId: user?.id || null });
          } catch (notificationError) {
            console.warn('Event notification failed:', notificationError?.message || notificationError);
          }
        }
      }

      await loadEvents();
      setIsFormOpen(false);
      setEditingEvent(null);
      setForm(emptyForm);
    } catch (err) {
      setError(`Failed to save event: ${formatSupabaseError(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async () => {
    if (!deleteTarget || !hoaId) return;
    setSaving(true);
    setError('');
    try {
      const { error: deleteError } = await withSupabaseQueryTimeout(
        supabase
        .from('board_meetings')
        .delete()
        .eq('hoa_id', hoaId)
          .eq('id', deleteTarget.id),
        'Deleting event'
      );
      if (deleteError) throw deleteError;
      logEvent('event_deleted', { event_id: deleteTarget.id, event_title: deleteTarget.title });
      setDeleteTarget(null);
      await loadEvents();
    } catch (err) {
      setError(`Failed to delete event: ${err.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteMessage = deleteTarget?.scheduled_at
    ? `"${deleteTarget?.title}" scheduled for ${formatEventDate(deleteTarget?.scheduled_at)} will be permanently removed. This cannot be undone.`
    : `"${deleteTarget?.title}" will be permanently removed. This cannot be undone.`;

  return (
    <div className="space-y-8">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-medium flex items-center justify-between gap-4">
          <span>{error}</span>
          <button type="button" onClick={loadEvents} className="border border-red-200 px-4 py-2 rounded-full font-bold hover:bg-red-100 transition-all">
            Retry
          </button>
        </div>
      )}

      <section className="bg-section-bg p-8 rounded-[2.5rem] space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center warm-tactile-shadow">
              <span className="material-symbols-outlined text-2xl text-secondary">calendar_today</span>
            </div>
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-primary-dark">Events</h2>
              <p className="text-sm font-medium text-slate-500 mt-1">
                {upcoming.length} upcoming · {past.length} past events
              </p>
            </div>
          </div>
          {canManage && (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center justify-center gap-2 bg-secondary text-white px-6 py-3 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all"
            >
              <span className="material-symbols-outlined text-xl">add_circle</span>
              New Event
            </button>
          )}
        </div>

        <div className="space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">Upcoming</h3>
          {loading ? (
            <div className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 p-8 text-center text-sm font-medium text-slate-500">
              Loading events...
            </div>
          ) : upcoming.length > 0 ? (
            upcoming.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                canManage={canManage}
                isPast={false}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
              />
            ))
          ) : (
            <EventEmptyState />
          )}
        </div>

        {past.length > 0 && (
          <div className="space-y-4 pt-4">
            <button
              type="button"
              onClick={() => setPastOpen((prev) => !prev)}
              className="w-full flex items-center justify-between gap-3 text-left border-t border-white/70 pt-6"
            >
              <span className="text-xs font-bold uppercase tracking-widest text-slate-500">
                Past Events ({past.length})
              </span>
              <span className="material-symbols-outlined text-secondary">{pastOpen ? 'expand_less' : 'expand_more'}</span>
            </button>
            {pastOpen && past.map((event) => (
              <EventRow
                key={event.id}
                event={event}
                canManage={canManage}
                isPast
                onEdit={openEdit}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" role="presentation" data-modal-backdrop="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeForm}
          >
            <motion.div
              className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Create or edit event"
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(modalEvent) => modalEvent.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 bg-secondary/10 rounded-xl flex items-center justify-center">
                    <span className="material-symbols-outlined text-secondary text-2xl">
                      {editingEvent ? 'edit' : 'add_circle'}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-primary-dark">{editingEvent ? 'Edit Event' : 'Create Event'}</h3>
                    <p className="text-sm font-medium text-slate-500">Set the event details and role audience.</p>
                  </div>
                </div>
                <button type="button" onClick={closeForm} aria-label="Close event dialog" className="w-10 h-10 rounded-full hover:bg-section-bg flex items-center justify-center">
                  <span className="material-symbols-outlined text-xl">close</span>
                </button>
              </div>

              <form onSubmit={saveEvent} className="space-y-5">
                <div className="p-5 bg-section-bg rounded-2xl border border-amber-100/30 space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">Event title</label>
                    <input
                      type="text"
                      required
                      value={form.title}
                      onChange={(inputEvent) => setForm((prev) => ({ ...prev, title: inputEvent.target.value }))}
                      className="w-full px-4 py-3 bg-white border border-amber-100/30 hover:border-secondary/40 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-primary-dark placeholder-slate-400 text-sm font-medium outline-none transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">Type</label>
                      <select
                        value={form.meeting_type}
                        onChange={(inputEvent) => setForm((prev) => ({ ...prev, meeting_type: inputEvent.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-amber-100/30 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-primary-dark text-sm font-medium outline-none transition-all"
                      >
                        {EVENT_TYPES.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">Date</label>
                      <input
                        type="date"
                        value={form.date}
                        onChange={(inputEvent) => setForm((prev) => ({ ...prev, date: inputEvent.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-amber-100/30 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-primary-dark text-sm font-medium outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">Time</label>
                      <input
                        type="time"
                        value={form.time}
                        onChange={(inputEvent) => setForm((prev) => ({ ...prev, time: inputEvent.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-amber-100/30 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-primary-dark text-sm font-medium outline-none transition-all"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">Location</label>
                      <input
                        type="text"
                        value={form.location}
                        onChange={(inputEvent) => setForm((prev) => ({ ...prev, location: inputEvent.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-amber-100/30 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-primary-dark text-sm font-medium outline-none transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-widest">Meeting link</label>
                      <input
                        type="url"
                        value={form.meeting_url}
                        onChange={(inputEvent) => setForm((prev) => ({ ...prev, meeting_url: inputEvent.target.value }))}
                        className="w-full px-4 py-3 bg-white border border-amber-100/30 focus:border-secondary focus:ring-2 focus:ring-secondary/10 rounded-xl text-primary-dark text-sm font-medium outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                <AudienceCheckboxGroup
                  value={form.visible_to_roles}
                  onChange={(next) => setForm((prev) => ({ ...prev, visible_to_roles: next }))}
                />

                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="border border-slate-200 text-slate-700 px-6 py-2.5 rounded-full font-bold text-sm hover:bg-section-bg transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving || form.visible_to_roles.length === 0 || !form.title.trim()}
                    className="bg-secondary text-white px-6 py-2.5 rounded-full font-bold text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : editingEvent ? 'Save Changes' : 'Create Event'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Page-level only: do not move this ConfirmDialog inside another <Modal>; use an inline panel there. */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete this event?"
        message={deleteMessage}
        confirmLabel="Delete event"
        variant="danger"
        onConfirm={deleteEvent}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default EventManagement;
